/* Partner portal — global provisioning-quantity sync.
 *
 * Loaded on every page (one deferred script tag from
 * snippets/pp-cart-sync.liquid). Full no-op unless the cart contains
 * portal-tagged phone lines, so it costs non-portal shoppers one
 * conditional fetch listener and nothing else.
 *
 * Contract: portal phone lines carry the private line property
 * _pp_provision = "1"; the provisioning variant id is stored in the
 * cart attribute _pp_prov_variant (written by pp.js at add time).
 * This script keeps the provisioning line's quantity equal to the sum
 * of eligible phone units, no matter where the cart is edited.
 *
 * Wiring notes (verified against theme.js):
 *  - 'cart:rerendered' does NOT bubble when dispatched from the cart
 *    drawer (theme.js ~2074) but DOES from the cart page (~2090), so we
 *    bind on the header cart element AND on document.
 *  - after changing the cart we dispatch 'cart:refresh' (non-opening);
 *    the resulting re-render fires 'cart:rerendered' again, which
 *    re-enters sync() and no-ops because quantities now match.
 */
(function () {
  'use strict';
  if (window.__ppCartSync) return;
  window.__ppCartSync = true;

  var busy = false;
  var pending = false; // a sync request arrived while one was running
  var lastCart = null;

  function cartUrl(path) {
    var base = (window.routes && window.routes.cartUrl) || '/cart';
    return base + path;
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    });
  }

  function done() {
    busy = false;
    if (pending) { pending = false; sync(); }
  }

  function sync() {
    if (busy) { pending = true; return; }
    busy = true;
    fetch(cartUrl('.js'), { credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCart = cart;
        var provVariant = parseInt(cart.attributes && cart.attributes._pp_prov_variant, 10) || 0;
        var eligible = 0;
        var provLine = null;
        (cart.items || []).forEach(function (item) {
          if (item.properties && item.properties._pp_provision) eligible += item.quantity;
          if (provVariant && item.variant_id === provVariant) provLine = item;
        });

        // Nothing portal-related in this cart at all — the common case.
        if (!provVariant || (eligible === 0 && !provLine)) { done(); return; }

        var current = provLine ? provLine.quantity : 0;
        if (current === eligible) { done(); return; }

        var op;
        if (provLine) {
          op = post(cartUrl('/change.js'), { id: String(provVariant), quantity: eligible });
        } else {
          op = post(((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js', {
            items: [{ id: provVariant, quantity: eligible, properties: { '_pp_portal': 'provisioning-sync' } }]
          });
        }
        return op.then(function () {
          done();
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { scrollToTop: false }
          }));
        });
      })
      .catch(function () { done(); });
  }

  // Direct hook for pp.js: sync immediately after a portal add instead of
  // waiting for a theme event that may not arrive.
  window.__ppCartSyncNow = sync;

  // Observe ALL cart writes — theme drawer, cart page, portal, or third-party
  // drawers (UpCart) that never fire the theme's cart events. Any /cart/
  // mutation schedules a reconcile; sync() no-ops when quantities already
  // match, and its own change.js call therefore cannot loop.
  var origFetch = window.fetch;
  window.fetch = function () {
    var result = origFetch.apply(this, arguments);
    try {
      var url = typeof arguments[0] === 'string' ? arguments[0] : ((arguments[0] && arguments[0].url) || '');
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        result.then(function () { setTimeout(sync, 200); }, function () {});
      }
    } catch (e) { /* never break the caller's fetch */ }
    return result;
  };

  // Drawer accessory offers (snippets/pp-drawer-offers.liquid): one-click add.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-pp-drawer-add]') : null;
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    var id = parseInt(btn.getAttribute('data-pp-drawer-add'), 10);
    if (!id) { btn.disabled = false; return; }
    post(((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js', {
      items: [{ id: id, quantity: 1, properties: { '_pp_portal': 'drawer-offer' } }]
    }).then(function (r) {
      if (r.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: 1 }
        }));
      } else {
        btn.disabled = false;
      }
    }).catch(function () { btn.disabled = false; });
  });

  // Partner pricing hand-off. Discount codes set via cookie/URL are dropped
  // by Shop Pay's accelerated checkout (verified), but codes attached to a
  // Storefront API cart survive every checkout path. So when a portal cart
  // heads to checkout we rebuild it as a Storefront cart (same lines, same
  // line properties, same attributes, plus the codes) and send the buyer to
  // that cart's checkoutUrl. Any failure falls back to the normal checkout.
  function partnerCheckout(cart) {
    var codes = String(cart.attributes._pp_codes || '').split(',').filter(Boolean);
    var token = cart.attributes._pp_sf;
    if (!codes.length || !token) return Promise.reject(new Error('no codes/token'));
    var lines = (cart.items || []).map(function (i) {
      var attrs = [];
      Object.keys(i.properties || {}).forEach(function (k) {
        var v = i.properties[k];
        if (v !== null && v !== undefined && String(v) !== '') attrs.push({ key: k, value: String(v) });
      });
      return { merchandiseId: 'gid://shopify/ProductVariant/' + i.variant_id, quantity: i.quantity, attributes: attrs };
    });
    var cattrs = [];
    Object.keys(cart.attributes || {}).forEach(function (k) {
      var v = cart.attributes[k];
      if (v && k !== '_pp_sf') cattrs.push({ key: k, value: String(v) });
    });
    var query = 'mutation($input: CartInput!) { cartCreate(input: $input) { cart { checkoutUrl } userErrors { message } } }';
    return fetch('https://headsetadvisor.myshopify.com/api/2024-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query: query, variables: { input: { lines: lines, discountCodes: codes, attributes: cattrs } } })
    }).then(function (r) { return r.json(); }).then(function (j) {
      var c = j && j.data && j.data.cartCreate;
      if (!c || !c.cart || !c.cart.checkoutUrl) throw new Error((c && c.userErrors && c.userErrors.map(function (e) { return e.message; }).join('; ')) || 'cartCreate failed');
      return c.cart.checkoutUrl;
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]') : null;
    if (!btn || !lastCart) return;
    var codes = lastCart.attributes && lastCart.attributes._pp_codes;
    var hasPortal = (lastCart.items || []).some(function (i) { return i.properties && i.properties._pp_portal; });
    if (!codes || !hasPortal) return;
    e.preventDefault();
    e.stopPropagation();
    var label = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    if (btn.tagName === 'BUTTON') { btn.disabled = true; btn.textContent = 'Applying partner pricing\u2026'; }
    fetch(cartUrl('.js'), { credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache' } })
      .then(function (r) { return r.json(); })
      .then(partnerCheckout)
      .then(function (url) { window.location.href = url; })
      .catch(function () {
        // fallback: cookie route (works for regular checkout when a single code applies)
        window.location.href = '/discount/' + encodeURIComponent(codes) + '?redirect=%2Fcheckout';
      });
  }, true);

  function bind() {
    // Cart-page edits: bubbles to document.
    document.addEventListener('cart:rerendered', sync);
    // Drawer edits: event does not bubble — bind on the element itself.
    var headerCart = document.querySelector('.header__action-item--cart');
    if (headerCart) headerCart.addEventListener('cart:rerendered', sync);
    // Reconcile once per page load (covers edits made without JS, etc.).
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
